//
// Copyright (c) 2026 Comcast
//

import listenerMiddleware from 'listenerMiddleware'
import { locationChanged, navigate } from 'reducers/routing'

let unsubscribe = null
let initialized = false
let currentRouter = null
let stopNavigateListening = null

export const initRouterListener = (store, router) => {
  if (initialized && currentRouter === router) {
    return
  }

  if (unsubscribe) {
    unsubscribe()
    unsubscribe = null
  }

  currentRouter = router
  initialized = true

  let prevKey = null
  const notify = () => {
    const { pathname, search, hash, state, key } = router.state.location || {}
    const currKey = `${pathname || ''}|${search || ''}|${hash || ''}`
    if (prevKey !== currKey) {
      prevKey = currKey
      store.dispatch(locationChanged({ pathname, search, hash, state, key }))
    }
  }

  // initial emit to align with previous behavior if needed
  notify()
  unsubscribe = router.subscribe(() => notify())
}

// Translate routing/navigate command action into router.navigate calls
stopNavigateListening = listenerMiddleware.startListening({
  actionCreator: navigate,
  effect: (action) => {
    if (!currentRouter) {
      return
    }
    const payload = action.payload
    // Relative history navigation: numeric payload offsets history (e.g., -1)
    if (typeof payload === 'number') {
      currentRouter.navigate(payload)
      return
    }
    if (typeof payload === 'string') {
      currentRouter.navigate(payload)
      return
    }
    const { to, options } = payload || {}
    // Relative history navigation via object: `to` as number offsets history (e.g., -1)
    if (typeof to === 'number') {
      currentRouter.navigate(to)
      return
    }
    if (to) {
      currentRouter.navigate(to, options)
    }
  },
})

// HMR cleanup: in development with Webpack's HMR, detach subscriptions
// to avoid duplicate listeners after hot updates. No effect in production
// because `module.hot` is undefined.
if (module?.hot) {
  // Accept hot updates so dispose runs before replacement
  module.hot.accept()
  module.hot.dispose(() => {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    if (stopNavigateListening) {
      stopNavigateListening()
      stopNavigateListening = null
    }
    initialized = false
    currentRouter = null
  })
}
